import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { UseFormReturn } from "react-hook-form";

import {
  isValidEmail,
  isValidMobile,
  matchCountryRegex,
  sentryLogs,
  timezone,
} from "@/utils";
import { RAZOR_KEY_ID } from "@/config";
import { DEFAULT_API_ERROR_MESSAGE } from "@/utils/Constants";
import { useAppDispatch } from "@/hooks";

import { FormValuesI } from "./useRegisterForm";

import theme from "@/foundation/Theme";

import * as actions from "@/redux/LandingPageReducer";

import logo from "@/static/logo-mh-razorpay.png";

interface UseFormHandlerProps {
  methods: UseFormReturn<FormValuesI>;
  mapData: () => {};
  pricing: {
    slug: string;
    price: number | string;
    currency: string;
    package_duration: number;
  };
  setSuccess: (success: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
  paymentSuccessPath?: string | { pathname: string; search: any };
  paymentFailurePath?: string;
}

const useFormHandler = ({
  methods,
  mapData,
  pricing,
  setSuccess,
  setIsLoading,
  paymentSuccessPath = "/payments/success",
  paymentFailurePath = "/payments/failure",
}: UseFormHandlerProps) => {
  const dispatch = useAppDispatch();
  const router = useRouter();

  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const {
    formState: { errors },
    reset,
    getValues,
  } = methods;

  const handleBlur = () => {
    const { mobile, mobileCountryCode, email } = getValues();

    const isValidContactDetail =
      (isValidMobile(mobile) && matchCountryRegex(mobileCountryCode, mobile)) ||
      isValidEmail(email);

    if (isValidContactDetail) {
      const apiData = {
        is_lead: true,
        ...mapData(),
      };
      dispatch(actions.saveLead({ data: apiData }));
    }
  };

  const onSuccessCallback = ({
    razorpay_payment_id,
    amount,
  }: {
    razorpay_payment_id: string;
    amount: number | string;
  }) => {
    const apiData = {
      ...mapData(),
      is_payment_completed: true,
      payment_mode: "razorpay",
      transaction_id: razorpay_payment_id,
      purchased_plan_slug: pricing?.slug,
      is_callback_request: false,
      amount_paid: amount,
      special_pack_purchase: true,
    };
    dispatch(actions.saveLead({ data: apiData }))
      .then((response) => {
        if (response?.payload?.status) {
          let query = {
            ...router.query,
            package_code: apiData?.package_slug,
            leadid: response?.payload?.lead_id,
            amount_paid: apiData?.amount_paid,
          };
          if (typeof paymentSuccessPath === "object") {
            query = {
              ...query,
              ...(paymentSuccessPath?.search || {}),
            };
          }

          let queryString = new URLSearchParams(query).toString();

          if (apiData?.urls_arguments)
            queryString = `${queryString}&${apiData?.urls_arguments?.slice(1)}`;

          if (typeof paymentSuccessPath === "string") {
            router.push({
              pathname: paymentSuccessPath,
              search: queryString,
            });
          } else {
            router.push({
              pathname: paymentSuccessPath?.pathname,
              search: queryString,
            });
          }
        } else {
          router.push({
            pathname: paymentFailurePath,
          });
          sentryLogs(
            "error",
            `LANDINGPAGE - PAYMENT BACKEND CALL FAILURE - ${apiData?.name}`,
            response.data
          );
        }
      })
      .catch((error) => {
        sentryLogs(
          "error",
          `LANDINGPAGE - PAYMENT BACKEND CALL FAILURE - ${apiData?.name}`,
          error
        );
      });
  };

  const onDismiss = () => {
    setIsLoading(false);
    const apiData = {
      is_lead: true,
      ...mapData(),
      payment_mode: "razorpay",
      payment_stage: "payment_cancel",
    };
    dispatch(actions.saveLead({ data: apiData })).then(() => {
      setIsLoading(false);
    });
  };

  const [razorPayError, setRazorPayError] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      document.body.appendChild(script);
    }, 5000);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  const openPaymentModal = (
    totalPrice,
    currency,
    desc,
    callback = () => {}
  ) => {
    const { name, mobileCountryCode, mobile, email } = getValues();

    let razorPayOptions = {
      key: RAZOR_KEY_ID,
      amount: totalPrice * 100, //  100 = 1 INR
      currency: currency,
      name: getValues("name"),
      description: desc,
      image: logo,
      handler: function (response) {
        if (response.razorpay_payment_id) {
          onSuccessCallback({ ...response, amount: totalPrice });
        }
      },
      prefill: {
        contact: "",
        email: "",
      },
      notes: {
        name,
        primary_contact: (mobileCountryCode || "+91") + mobile,
        ...(email && {
          email: email,
        }),
        package_slug: mapData()?.package_slug,
        urls_arguments: mapData()?.urls_arguments,
        purchased_plan_slug: pricing?.slug,
        special_pack_purchase: true,

        plan_start_date: mapData()?.plan_start_date,
        centertiming_id: mapData()?.centertiming_id,
        timezone: timezone,
      },
      theme: {
        color: theme.colorsV2.shyft.purple,
      },
      modal: {
        ondismiss: async function () {
          await onDismiss();
        },
      },
    };

    callback();
    razorPayOptions = {
      ...razorPayOptions,
      amount: totalPrice * 100,
      currency: currency,
      description: desc,
      secondaryBuy: false,
      prefill: {
        name,
        contact: (mobileCountryCode || "+91") + mobile,
        email: email || "",
      },
    };

    const onLoad = () => {
      let rzp1 = new window.Razorpay(razorPayOptions);
      rzp1.open();
    };
    if (!window.Razorpay) {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.defer = true;
      document.body.appendChild(script);
      script.onload = onLoad;

      script.onerror = () => {
        setRazorPayError(DEFAULT_API_ERROR_MESSAGE);
        sentryLogs(
          "error",
          "Razorpay library not loaded hence Razorpay is undefined."
        );
      };
    } else {
      onLoad();
    }
  };

  const openPaymentPage = (data) => {
    const totalPrice = data?.price;
    const currency = data?.currency;
    const desc = data?.slug || data?.package_duration;
    const callback = () => {
      // dispatch(resetStatusValues());
    };
    openPaymentModal(totalPrice, currency, desc, callback);
  };

  const onSubmit = (state) => {
    setIsLoading(true);
    const _apiData = mapData();
    if (state) {
      let apiData = {
        is_callback_request: true,
        ..._apiData,
      };

      dispatch(actions.saveLead({ data: apiData })).then(() => {
        setSuccess(true);
        setIsLoading(false);
        setShowSuccessModal(true);
      });
    } else {
      let apiData = {
        event_state: "payment_initiated",
        ..._apiData,
      };

      dispatch(actions.saveLead({ data: apiData }));
      openPaymentPage(pricing);
    }
  };

  const onSubmitCallback = (state) => {
    if (Object.keys(errors)?.length === 0) {
      onSubmit(state);
    }
  };

  return {
    handleBlur,
    onSubmitCallback,
  };
};

export default useFormHandler;
